import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/servizi", label: "Trattamenti" },
  { to: "/chi-sono", label: "Chi Sono" },
  { to: "/eventi", label: "Eventi" },
  { to: "/contatti", label: "Contatti" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between rounded-full bg-cream-soft/70 px-5 py-2.5 shadow-[0_12px_32px_rgba(48,51,48,0.06)] backdrop-blur-xl sm:px-7 mx-4 sm:mx-auto">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Artemisia" className="h-10 w-10 rounded-full object-cover" />
            <span className="font-serif text-xl italic text-sage-deep">Artemisia Benessere</span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm transition-colors duration-300 ${
                    isActive ? "font-semibold text-sage-deep" : "text-ink/60 hover:text-sage-deep"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contatti"
              data-testid="nav-cta-prenota"
              className="hidden rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-6 py-2.5 text-sm font-medium text-cream transition-transform duration-500 hover:scale-[1.04] sm:block"
            >
              Prenota
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Apri menu"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-sage-mist text-sage-deep lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-4 top-24 z-40 rounded-[2rem] bg-cream-soft/95 p-8 shadow-[0_24px_64px_rgba(48,51,48,0.12)] backdrop-blur-2xl lg:hidden"
          >
            <nav className="flex flex-col gap-5">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-serif text-2xl text-ink"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
